import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../api';
import './AdminImportPage.css';

const COOKIE_KEY = 'dnrRedSessionCookie';
const FINGERPRINT_KEY = 'dnrRedFingerprint';
const DELAY_BETWEEN_IMPORTS_MS = 500;

const STATUS_TEXT = { Published: 'Опубликовано', Draft: 'Черновик (есть ошибки, нужна проверка)' };

const AdminImportPage = () => {
    const [importUrl, setImportUrl] = useState('');
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState(null);
    const [result, setResult] = useState(null); // { adId, phoneRevealAttempted, phoneRevealSucceeded, phoneRevealError, photosImported, photosFailed, photoErrors }

    const [sessionCookie, setSessionCookie] = useState(() => sessionStorage.getItem(COOKIE_KEY) || '');
    const [fingerprint, setFingerprint] = useState(() => sessionStorage.getItem(FINGERPRINT_KEY) || '');
    const [saved, setSaved] = useState(false);

    const [bulkUrls, setBulkUrls] = useState('');
    const [bulkRunning, setBulkRunning] = useState(false);
    const [bulkResults, setBulkResults] = useState([]); // [{ url, status: 'pending'|'ok'|'error', adId, adStatus, message }]

    useEffect(() => {
        if (!bulkRunning) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [bulkRunning]);

    const saveSettings = (e) => {
        e.preventDefault();
        sessionStorage.setItem(COOKIE_KEY, sessionCookie);
        sessionStorage.setItem(FINGERPRINT_KEY, fingerprint);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    const importAd = async (e) => {
        e.preventDefault();
        if (!importUrl.trim()) return;
        setImporting(true);
        setImportError(null);
        setResult(null);
        try {
            const { data } = await adminService.importAd(importUrl.trim(), sessionCookie || null, fingerprint || null);
            setResult({
                adId: data.id,
                status: data.status,
                phoneRevealAttempted: data.phoneRevealAttempted,
                phoneRevealSucceeded: data.phoneRevealSucceeded,
                phoneRevealError: data.phoneRevealError,
                photosImported: data.photosImported,
                photosFailed: data.photosFailed,
                photoErrors: data.photoErrors || [],
            });
            if (data.phoneRevealAttempted && !data.phoneRevealSucceeded) {
                console.error('Phone reveal failed:', data.phoneRevealError);
            }
            if (data.photosFailed > 0) {
                console.error('Photo import errors:', data.photoErrors);
            }
        } catch (err) {
            console.error('Ad import failed:', err);
            setImportError(err.response?.data?.error || 'Не удалось импортировать объявление.');
        } finally {
            setImporting(false);
        }
    };

    const runBulkImport = async (e) => {
        e.preventDefault();
        const urls = bulkUrls.split('\n').map(u => u.trim()).filter(Boolean);
        if (urls.length === 0) return;

        setBulkRunning(true);
        setBulkResults(urls.map(url => ({ url, status: 'pending' })));

        // Sequential, one URL at a time, with a delay between requests to avoid tripping
        // dnr.red's rate limiting; a single failed URL doesn't stop the rest of the batch.
        for (let i = 0; i < urls.length; i++) {
            const url = urls[i];
            try {
                const { data } = await adminService.importAd(url, sessionCookie || null, fingerprint || null);
                setBulkResults(prev => prev.map((r, idx) =>
                    idx === i ? { url, status: 'ok', adId: data.id, adStatus: data.status } : r
                ));
            } catch (err) {
                const message = err.response?.data?.error || 'Ошибка импорта';
                setBulkResults(prev => prev.map((r, idx) =>
                    idx === i ? { url, status: 'error', message } : r
                ));
            }
            if (i < urls.length - 1) {
                await new Promise(res => setTimeout(res, DELAY_BETWEEN_IMPORTS_MS));
            }
        }
        setBulkRunning(false);
    };

    return (
        <div className="admin-import">
            <h1 className="admin-import__title">Импорт объявлений</h1>

            <section className="admin-import__section">
                <h2 className="admin-import__subtitle">Импортировать по ссылке</h2>
                <p className="admin-import__hint">Поддерживается dnr.red: квартиры (аренда/посуточно/продажа) и продажа домов.</p>

                {importError && <div className="admin-import__error">{importError}</div>}

                <form className="admin-import__row" onSubmit={importAd}>
                    <input
                        className="admin-import__input"
                        placeholder="Ссылка на объявление на стороннем сайте"
                        value={importUrl}
                        onChange={e => setImportUrl(e.target.value)}
                        disabled={importing}
                    />
                    <button type="submit" className="admin-import__btn-save" disabled={importing}>
                        {importing ? 'Импортирую...' : 'Импортировать'}
                    </button>
                </form>

                {result && (
                    <div className="admin-import__result">
                        <span>{STATUS_TEXT[result.status] || 'Черновик создан.'}</span>
                        {result.phoneRevealAttempted && (
                            result.phoneRevealSucceeded
                                ? <span className="admin-import__phone-ok"> Телефон получен.</span>
                                : (
                                    <span className="admin-import__phone-fail">
                                        {' '}Телефон не получен — похоже, устарела сессия. Обнови Session Cookie / Fingerprint ниже и попробуй ещё раз (или у объявления просто нет номера).
                                        {result.phoneRevealError && (
                                            <><br />Причина: <code>{result.phoneRevealError}</code></>
                                        )}
                                    </span>
                                )
                        )}
                        {typeof result.photosImported === 'number' && (
                            result.photosFailed > 0 ? (
                                <div className="admin-import__phone-fail">
                                    Фото: загружено {result.photosImported}, не удалось {result.photosFailed}.
                                    <ul>
                                        {result.photoErrors.map((e, i) => <li key={i}><code>{e}</code></li>)}
                                    </ul>
                                </div>
                            ) : (
                                <div className="admin-import__phone-ok">Фото загружено: {result.photosImported}.</div>
                            )
                        )}
                        <Link
                            className="admin-import__btn-open"
                            to={`/admin/ads/edit/${result.adId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Открыть черновик →
                        </Link>
                    </div>
                )}
            </section>

            <section className="admin-import__section">
                <h2 className="admin-import__subtitle">Массовый импорт</h2>
                <p className="admin-import__hint">
                    По ссылке на строку. Обрабатываются по одной с задержкой между запросами, чтобы не словить бан на dnr.red.
                    Уже импортированные ранее ссылки будут пропущены автоматически.
                </p>

                <form onSubmit={runBulkImport}>
                    <textarea
                        className="admin-import__textarea"
                        rows={6}
                        placeholder={'https://dnr.red/...\nhttps://dnr.red/...'}
                        value={bulkUrls}
                        onChange={e => setBulkUrls(e.target.value)}
                        disabled={bulkRunning}
                    />
                    <button type="submit" className="admin-import__btn-save" disabled={bulkRunning}>
                        {bulkRunning ? 'Импортирую...' : 'Импортировать все'}
                    </button>
                </form>

                {bulkResults.length > 0 && (
                    <ul className="admin-import__bulk-list">
                        {bulkResults.map((r, i) => (
                            <li key={i} className={`admin-import__bulk-item admin-import__bulk-item--${r.status}`}>
                                <span className="admin-import__bulk-url">{r.url}</span>
                                {r.status === 'pending' && <span>…</span>}
                                {r.status === 'ok' && (
                                    <span className="admin-import__phone-ok">
                                        {STATUS_TEXT[r.adStatus] || 'Импортировано'}
                                        {' — '}
                                        <Link to={`/admin/ads/edit/${r.adId}`} target="_blank" rel="noopener noreferrer">открыть</Link>
                                    </span>
                                )}
                                {r.status === 'error' && <span className="admin-import__phone-fail">{r.message}</span>}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="admin-import__section">
                <h2 className="admin-import__subtitle">Настройки dnr.red</h2>
                <p className="admin-import__hint">
                    Нужны, чтобы подтягивать телефон продавца. Возьми из DevTools своего залогиненного браузера:
                    cookie <code>session=...</code> и cookie <code>br_fingerprint</code>. Хранятся только в этом
                    браузере (sessionStorage), на сервер не сохраняются. Без них импорт всё равно работает, просто
                    без телефона (подставится твой контакт).
                </p>

                <form className="admin-import__form" onSubmit={saveSettings}>
                    <label className="admin-import__label">
                        Session cookie
                        <input
                            className="admin-import__input-full"
                            value={sessionCookie}
                            onChange={e => setSessionCookie(e.target.value)}
                            placeholder="session=..."
                        />
                    </label>

                    <label className="admin-import__label">
                        Fingerprint (br_fingerprint)
                        <input
                            className="admin-import__input-full"
                            value={fingerprint}
                            onChange={e => setFingerprint(e.target.value)}
                            placeholder="e853c3edeab28c25d348164aaee99982"
                        />
                    </label>

                    <div className="admin-import__form-actions">
                        <button type="submit" className="admin-import__btn-save">Сохранить в браузере</button>
                        {saved && <span className="admin-import__saved">Сохранено</span>}
                    </div>
                </form>
            </section>
        </div>
    );
};

export default AdminImportPage;
